import MapModel                  from '@models/map/MapModel';
import Form                      from '@widesk-ui/components/form/Form';
import Input                     from '@widesk-ui/components/form/Input';
import View                      from '@widesk-ui/components/View';
import ColorPicker               from '@widesk-ui/components/form/ColorPicker';
import React                     from 'react';
import { GlobalOutlined }        from '@ant-design/icons';
import { BgColorsOutlined }      from '@ant-design/icons';
import { CheckOutlined }         from '@ant-design/icons';
import useDesignTokens           from '@/hooks/useDesignTokens';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import nextFreeLabel             from '@/tools/nextFreeLabel';
import { DEFAULT_ORIGIN_CENTER } from '@/TokamapEditor/ux/constants';
import { AddressSearchResult }   from '@tokamap/core';
import { DEFAULT_FRAMING }       from '@tokamap/core';
import { lngLatToMercator }      from '@tokamap/core';
import { serializeFraming }      from '@tokamap/core';
import { message }               from '@widesk-ui/hooks/useMessage';
import PlaceSearchField          from '@forms/map/PlaceSearchField';

const PLACED_MAP_ZOOM = 17;

export type MapFormProps = {
	model: MapModel;
	onSuccess?: () => void;
	projectUrn?: Urn;
}

/**
 * Formulaire d'une carte.
 *
 * À la CRÉATION, il ne pose qu'une question : **la carte est-elle située dans le monde réel ?**
 * C'est une décision de nature, pas d'apparence — elle détermine si la carte s'ouvre sur un lieu
 * choisi, et si les visiteurs pourront se géolocaliser dessus. Seules les coordonnées du lieu sont
 * enregistrées, jamais l'adresse.
 *
 * Aucun exemple de LIEU dans les libellés, volontairement : un centre commercial, un salon ou un
 * musée sont des lieux réels, qui relèvent aussi bien de l'une que de l'autre branche. Les citer d'un
 * seul côté laissait croire que le critère est la nature du lieu, alors qu'il est de vouloir — ou non
 * — l'ancrer à des coordonnées.
 *
 * Le titre n'y est plus demandé : il est généré libre et se corrige ensuite, comme pour un type, un
 * champ ou un point d'intérêt. En MODIFICATION, ce formulaire n'est plus qu'un renommage — la
 * `configuration` appartient alors à l'éditeur, la renvoyer d'ici la ramènerait à ses valeurs de
 * création.
 */
export default function MapForm(props: MapFormProps) {
	const isNew = !props.model.id;
	const tokens = useDesignTokens();

	// Choix tenus en état plutôt qu'en champs de formulaire : ce ne sont pas des saisies, et c'est ce
	// qui permet de n'afficher chaque réglage secondaire que sous la branche à laquelle il appartient.
	const [located, setLocated] = React.useState(true);
	const [backgroundColor, setBackgroundColor] = React.useState('#FFFFFF');
	const [place, setPlace] = React.useState<AddressSearchResult | null>(null);

	// Calculé une seule fois : recalculé à chaque rendu, le nom aurait pu changer sous les pieds de
	// l'utilisateur entre l'ouverture et la validation.
	const generatedLabel = React.useRef(nextFreeLabel(
		MapModel.localize(),
		currentProjectEditorStore.get.mapCollection.map(m => m.label).filter(Boolean),
		{ numberFirst: true },
	)).current;

	return (
		<Form
			updateOnChange
			onSuccess={props.onSuccess}
			onDidMount={async formTools => {
				if (props.model.id) formTools.setFieldsValue({ label: props.model.label });
			}}
			fields={{
				label: {
					// Demandé au renommage seulement. À la création, le nom est généré : on n'a encore rien
					// vu de la carte au moment où il faudrait l'inventer.
					hidden: isNew,
					label: 'Titre',
					element: <Input autoFocus />,
					required: true,
					span: 24,
				},
			}}
			onSubmit={async (values) => {
				const { model } = props;

				if (isNew && located && !place) {
					message.error('Choisissez le lieu de la carte, ou « Non, un plan seul ».');
					throw new Error('Lieu de la carte manquant');
				}

				await model.patch({
					label: values.label ?? generatedLabel,
					project: props.projectUrn,

					...(isNew ? {
						position: currentProjectEditorStore.get.mapCollection.length,
						configuration: {
							// Une carte non située n'est pas géographique : on la pose sur un point arbitraire
							// (la France) pour que le moteur ait un repère, et c'est le premier tracé qui lui
							// donnera son cadrage réel.
							center: located && place ? lngLatToMercator(place.center) : DEFAULT_ORIGIN_CENTER,
							zoom: located ? PLACED_MAP_ZOOM : 5,
							rotation: 0,
							tileLayerName: located ? 'osm' : 'unset',
							backgroundColor: located ? undefined : backgroundColor,
							formatVersion: 2,
							framing: serializeFraming(DEFAULT_FRAMING),
						},
					} : {}),
				});

				return model;
			}}
		>
			{isNew && (
				<View gap="sm">
					<View bold size="small">Votre carte est-elle située dans le monde réel ?</View>

					<Choice
						icon={<GlobalOutlined />}
						label="Oui, sur la carte du monde"
						hint="Elle s'ouvrira directement sur le lieu choisi. Les visiteurs pourront s'y géolocaliser."
						selected={located}
						onSelect={() => setLocated(true)}
					>
						<View style={{ width: '100%' }}>
							<PlaceSearchField value={place} onChange={setPlace} />
						</View>
					</Choice>

					<Choice
						icon={<BgColorsOutlined />}
						label="Non, un plan seul"
						hint="Le plan se suffit à lui-même, sans coordonnées : pas de géolocalisation, pas de 3D."
						selected={!located}
						onSelect={() => setLocated(false)}
					>
						<ColorPicker
							showText
							value={backgroundColor}
							onChangeComplete={color => setBackgroundColor(color || '#FFFFFF')}
						/>
					</Choice>

					{/* Ni « définitif » ni « sans conséquence » : les deux seraient faux. Le dire ici évite à
					    la fois l'hésitation devant un choix qu'on croit irrévocable et la mauvaise surprise
					    de celui qui rebascule après avoir tracé la moitié de son plan. */}
					<View size="small" color={tokens.text.muted} lineHeight={1.4}>
						Ce choix se change plus tard, mais les formes déjà tracées devront alors être
						repositionnées à la main : c'est beaucoup plus simple à décider maintenant.
					</View>
				</View>
			)}
		</Form>
	);
}

/**
 * Une des deux natures de carte. Ses réglages propres sont rendus À L'INTÉRIEUR, et seulement quand
 * elle est choisie : c'est ce qui montre qu'ils dépendent d'elle, au lieu de flotter sous les deux.
 */
const Choice = (props: {
	icon: React.ReactNode;
	label: string;
	hint: string;
	selected: boolean;
	onSelect: () => void;
	children: React.ReactNode;
}) => {
	const tokens = useDesignTokens();

	return (
		<View
			gap="sm"
			p="sm"
			br={8}
			pointer
			bg={props.selected ? tokens.fill.subtle : tokens.surface.card}
			hover={{ bg: tokens.surface.subtle }}
			style={{ border: `1px solid ${props.selected ? tokens.color.primary : tokens.border.default}` }}
			onClick={props.onSelect}
		>
			<View row gap="sm">
				<View
					w={32}
					h={32}
					br={7}
					center
					color={tokens.color.primary}
					bg={tokens.fill.subtle}
					style={{ flexShrink: 0 }}
				>
					{props.icon}
				</View>

				<View flex gap={2}>
					<View bold size="small">{props.label}</View>

					<View size="small" color={tokens.text.muted} lineHeight={1.4}>{props.hint}</View>
				</View>

				{/* Toujours rendue, invisible hors sélection : sans ça les libellés se décalaient d'une
				    ligne à l'autre au changement de choix. */}
				<CheckOutlined
					style={{ flexShrink: 0, fontSize: 12, opacity: props.selected ? 1 : 0, color: tokens.color.primary }}
				/>
			</View>

			{/* `stopPropagation` : régler le détail ne doit pas se lire comme un nouveau clic sur la
			    tuile — sans effet ici, mais ce serait un piège au prochain réglage ajouté. */}
			{props.selected && (
				<View row pl={40} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
					{props.children}
				</View>
			)}
		</View>
	);
};
